import { forwardRef } from "react";

/**
 * LessonPrintDoc
 * ---------------
 * Off-screen, print-friendly version of a full lesson. Two roles:
 *
 *   1. Source of truth for multi-page PDF export (via html-to-image
 *      + jsPDF slicing in shareExport.exportNodeToPdf).
 *   2. Visible-only-when-printing fallback target: a `@media print`
 *      rule (see index.css) hides the on-screen LessonView and shows
 *      THIS node instead so print output is clean, deterministic,
 *      and free of duplicated dialog backdrops or 3-D book chrome.
 *
 * Rendered as A4-portrait at 96 DPI (794×~auto height). The document is
 * deliberately built from pure DOM elements (no Dialogs / Tooltips /
 * dropdowns) so it scrapes reliably to PNG and prints predictably.
 */
const LessonPrintDoc = forwardRef(function LessonPrintDoc({ lesson, meta }, ref) {
  if (!lesson) return null;
  const navy = "#1a2b4a";
  const gold = "#a8862e";
  const muted = "#5b6877";
  const sources = lesson?.sources_used || [];
  const commentators = lesson?.per_commentator || [];

  return (
    <div
      ref={ref}
      data-testid="lesson-print-doc"
      dir="rtl"
      style={{
        width: 794,            // A4 width @ 96dpi
        minHeight: 1123,        // A4 height — content can extend, slicer handles paging
        background: "#ffffff",
        color: navy,
        padding: 56,
        boxSizing: "border-box",
        fontFamily: '"Frank Ruhl Libre", "David Libre", "Noto Serif Hebrew", serif',
        lineHeight: 1.55,
      }}
    >
      {/* Brand header */}
      <header
        style={{
          borderBottom: `2px solid ${gold}`,
          paddingBottom: 14,
          marginBottom: 22,
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 24,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: "italic",
              letterSpacing: 6,
              textTransform: "uppercase",
              fontSize: 12,
              color: gold,
            }}
          >
            Torahya · תוריא
          </div>
          <h1
            style={{
              margin: "8px 0 4px",
              fontSize: 28,
              color: navy,
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          >
            {lesson.title || "שיעור תורה"}
          </h1>
          {lesson.subtitle && (
            <div style={{ fontSize: 15, color: muted }}>{lesson.subtitle}</div>
          )}
        </div>
        <div style={{ textAlign: "left", fontSize: 11, color: muted, lineHeight: 1.6 }}>
          {meta?.ref_he && <div>{meta.ref_he}</div>}
          <div>torahya.com</div>
        </div>
      </header>

      {/* Opening hook */}
      {lesson.opening_hook && (
        <Section title="פתח דבר" gold={gold}>
          <p style={pStyle}>{lesson.opening_hook}</p>
        </Section>
      )}

      {/* Intro */}
      {lesson.intro && (
        <Section title="פתיחה" gold={gold}>
          <p style={pStyle}>{lesson.intro}</p>
        </Section>
      )}

      {/* Simple meaning */}
      {lesson.simple_meaning && (
        <Section title="פשט — המשמעות הפשוטה" gold={gold}>
          <p style={pStyle}>{lesson.simple_meaning}</p>
        </Section>
      )}

      {/* Per-commentator */}
      {commentators.length > 0 && (
        <Section title="מה אומר כל פרשן" gold={gold}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {commentators.map((c, i) => (
              <article
                key={i}
                style={{
                  border: `1px solid ${gold}33`,
                  borderRight: `3px solid ${gold}`,
                  padding: "10px 14px",
                  borderRadius: 2,
                  pageBreakInside: "avoid",
                  breakInside: "avoid",
                }}
              >
                <header
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    marginBottom: 6,
                    gap: 8,
                  }}
                >
                  <strong style={{ color: gold, fontSize: 16 }}>
                    {c.commentator_he || c.commentator}
                  </strong>
                  {c.ref && (
                    <span style={{ fontSize: 11, color: muted, fontStyle: "italic" }}>
                      {c.ref}
                    </span>
                  )}
                </header>
                {c.not_available ? (
                  <p style={{ ...pStyle, fontStyle: "italic", color: muted }}>
                    אין פרשנות זמינה לפסוק זה.
                  </p>
                ) : (
                  <>
                    <p style={pStyle}>{c.summary}</p>
                    {c.insight && (
                      <p style={{ ...pStyle, marginTop: 6, fontStyle: "italic", color: navy }}>
                        ✦ {c.insight}
                      </p>
                    )}
                  </>
                )}
              </article>
            ))}
          </div>
        </Section>
      )}

      {/* Comparison */}
      {lesson.comparison && (lesson.comparison.agreements || lesson.comparison.differences) && (
        <Section title="השוואה בין הפרשנים" gold={gold}>
          {lesson.comparison.agreements && (
            <div style={{ marginBottom: 8 }}>
              <h4 style={{ ...subStyle, color: navy }}>נקודות הסכמה</h4>
              <p style={pStyle}>{lesson.comparison.agreements}</p>
            </div>
          )}
          {lesson.comparison.differences && (
            <div>
              <h4 style={{ ...subStyle, color: navy }}>נקודות מחלוקת</h4>
              <p style={pStyle}>{lesson.comparison.differences}</p>
            </div>
          )}
        </Section>
      )}

      {/* Deep development */}
      {lesson.deep_development && (
        <Section title="לב השיעור — פיתוח הרעיון" gold={gold}>
          {/* Markdown content; rendered as plain text fallback to avoid
              breaking print layout. The asterisks/headings won't be
              syntactically rendered, but readability stays high. */}
          <p style={{ ...pStyle, whiteSpace: "pre-wrap" }}>{lesson.deep_development}</p>
        </Section>
      )}

      {/* Main idea */}
      {lesson.main_idea && (
        <Section title="הרעיון המרכזי" gold={gold}>
          <p style={{ ...pStyle, fontSize: 17, color: navy }}>{lesson.main_idea}</p>
        </Section>
      )}

      {/* Modern relevance */}
      {lesson.modern_relevance && (
        <Section title="רלוונטיות לחיינו" gold={gold}>
          <p style={pStyle}>{lesson.modern_relevance}</p>
        </Section>
      )}

      {/* Takeaway */}
      {lesson.takeaway && (
        <Section title="לקח מעשי" gold={gold}>
          <p style={pStyle}>{lesson.takeaway}</p>
        </Section>
      )}

      {/* Questions */}
      {(lesson.questions || []).length > 0 && (
        <Section title="שאלות לדיון" gold={gold}>
          <ol style={{ paddingRight: 20, margin: 0, lineHeight: 1.7 }}>
            {lesson.questions.map((q, i) => (
              <li key={i} style={{ marginBottom: 4 }}>{q}</li>
            ))}
          </ol>
        </Section>
      )}

      {/* Sources */}
      {sources.length > 0 && (
        <Section title="מקורות שהשתמשנו בהם" gold={gold}>
          <ul style={{ paddingRight: 18, margin: 0, lineHeight: 1.7 }}>
            {sources.map((s, i) => (
              <li key={i} style={{ marginBottom: 2 }}>
                <strong>{s.commentator_he || s.commentator}</strong>
                {s.ref && <span style={{ color: muted, fontSize: 12 }}> — {s.ref}</span>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Footer */}
      <footer
        style={{
          marginTop: 28,
          paddingTop: 12,
          borderTop: `1px solid ${gold}55`,
          display: "flex",
          justifyContent: "space-between",
          fontSize: 11,
          color: muted,
        }}
      >
        <span>תוריא · בית המדרש הדיגיטלי</span>
        <span>torahya.com</span>
      </footer>
    </div>
  );
});

function Section({ title, gold, children }) {
  return (
    <section style={{ marginBottom: 18, pageBreakInside: "avoid", breakInside: "avoid" }}>
      <h2
        style={{
          margin: "0 0 8px",
          fontSize: 16,
          color: gold,
          fontWeight: 700,
          letterSpacing: 0.3,
          borderBottom: `1px solid ${gold}40`,
          paddingBottom: 4,
        }}
      >
        {title}
      </h2>
      <div>{children}</div>
    </section>
  );
}

const pStyle = { margin: 0, fontSize: 14, lineHeight: 1.7, color: "#1a2b4a" };
const subStyle = { margin: "0 0 4px", fontSize: 13, fontWeight: 700 };

export default LessonPrintDoc;
